import {
  QuotationStatus,
  type QuotationLineItemDto,
  type QuotationSubmissionSnapshot,
} from '../types/quotation.types';
import { isPostedRow } from './lineItem';
import {
  DEFAULT_VAT_RATE,
  LEGACY_SIZE_RULE,
  sizeRuleFromRecorded,
  type PricingBreakdown,
  type SizeRule,
} from './pricing';

// ── 14ykpqtr22u — posted quotations show what was posted ─────────────────────
// A posted quotation used to re-price itself live on every open, so a rule change (16mm → big) or a
// base-markup move rewrote its numbers on screen. Sales orders already render as-posted pricing
// (86d3c2wd1); this does the same for quotations, from the frozen submission snapshot.

/**
 * Which pricing a quotation shows. Keyed on STATUS, never on read-only: a quotation is also
 * read-only while its post is in flight (or failed after possibly reaching NetSuite), and it has
 * no snapshot yet then — status and snapshot are written together when the post completes.
 *  - 'posted'     — Submitted/Sent with a snapshot: read the snapshot.
 *  - 'unrecorded' — Submitted/Sent without one (the first posts, 27 Apr 2026): today's live
 *                   recompute under the legacy rule, labelled as such.
 *  - 'live'       — everything else: the current rule, live.
 */
export type PricingView = 'posted' | 'unrecorded' | 'live';

export function selectPricingView(
  status: QuotationStatus,
  snapshot: QuotationSubmissionSnapshot | null | undefined,
): PricingView {
  const isPosted = status === QuotationStatus.Submitted || status === QuotationStatus.Sent;
  if (!isPosted) return 'live';
  return snapshot ? 'posted' : 'unrecorded';
}

type SnapshotLine = NonNullable<QuotationSubmissionSnapshot['lines']>[number];

// One index per snapshot object (snapshots are immutable once loaded), so pricing N rows is O(N).
const linesByItemCache = new WeakMap<QuotationSubmissionSnapshot, Map<string, SnapshotLine>>();

function linesByItem(snapshot: QuotationSubmissionSnapshot): Map<string, SnapshotLine> {
  let index = linesByItemCache.get(snapshot);
  if (!index) {
    index = new Map((snapshot.lines ?? []).map((l) => [l.matchedItemId, l] as const));
    linesByItemCache.set(snapshot, index);
  }
  return index;
}

function gradeMarkupPercent(snapshot: QuotationSubmissionSnapshot, gradeCode: string | null): number | null {
  const gm = snapshot.header?.gradeMarkups;
  if (!gm || !gradeCode) return null;
  switch (gradeCode) {
    case '33': return gm.grade33;
    case '40': return gm.grade40;
    case '60': return gm.grade60;
    case '75': return gm.grade75;
    case '80': return gm.grade80;
    default:   return null;
  }
}

/**
 * The as-posted pricing for one row: the snapshot line for its item (the claim merges rows by
 * item, so there is exactly one), never a re-derivation — re-deriving from the row's rate misses
 * by a cent where the rate was stored at 4 decimals (e.g. QD-202604-00024: ₱728.25 vs ₱728.24
 * posted). Two rows of the same item both show the posted (merged) price — that is what NetSuite
 * received. Null ("—") for rows that never posted, and where the posted price is null (Grade 33,
 * Custom price level, and the handful of quotations posted without rates).
 */
export function postedRowPricing(
  li: QuotationLineItemDto,
  snapshot: QuotationSubmissionSnapshot,
  taxRate: number | null,
): PricingBreakdown | null {
  if (!isPostedRow(li) || !li.matchedItemId || li.quantity == null) return null;
  const line = linesByItem(snapshot).get(li.matchedItemId);
  if (!line || line.baseRate == null || line.finalUnitPrice == null) return null;

  const gm = gradeMarkupPercent(snapshot, line.gradeCode);
  const markupPct =
    gm != null && gm > 0 ? gm / 100 : line.baseRate > 0 ? line.finalUnitPrice / line.baseRate - 1 : 0;
  const amount = line.finalUnitPrice * li.quantity;
  return {
    rate: line.baseRate,
    markupPct,
    rateWithMarkup: line.finalUnitPrice,
    amount,
    grossAmount: amount * (1 + (taxRate ?? DEFAULT_VAT_RATE)),
  };
}

export interface PostedPricingSummary {
  /** Schema v1 (posted 27 Apr – 7 May 2026) recorded no tier or size mix. */
  isV1: boolean;
  tier: string | null;
  sizeMix: string | null;
  /** Tier adjustment as a fraction (0.04 = +4 points); null when not recorded. */
  adjustment: number | null;
  /** Small share as a fraction in [0, 1] — the snapshot stores it as a percent. */
  smallPct: number | null;
  totalWeightKg: number | null;
  /** At least one posted line carries a price (false for the quotations posted without rates). */
  hasAnyPrice: boolean;
  /** The size rule it was posted under — drives the weight split and the captions. */
  rule: SizeRule;
}

export function summarizePostedPricing(snapshot: QuotationSubmissionSnapshot): PostedPricingSummary {
  const totals = snapshot.header?.totals ?? null;
  const isV1 = (snapshot.schemaVersion ?? 1) < 2;
  return {
    isV1,
    tier: isV1 ? null : totals?.tier ?? null,
    sizeMix: isV1 ? null : totals?.sizeMix ?? null,
    adjustment: isV1 ? null : totals?.tierAdjustmentPct ?? null,
    smallPct: totals?.smallSizePct != null ? totals.smallSizePct / 100 : null,
    totalWeightKg: totals?.totalWeightKg ?? null,
    hasAnyPrice: (snapshot.lines ?? []).some((l) => l.finalUnitPrice != null),
    rule: totals ? sizeRuleFromRecorded(totals.smallDiametersMm) : LEGACY_SIZE_RULE,
  };
}

/** The posted lines as weight rows for computeWeightBreakdown (lineWeightKg → weightKg). */
export function postedWeightRows(
  snapshot: QuotationSubmissionSnapshot,
): Array<{ diameterMm: number | null; weightKg: number | null }> {
  return (snapshot.lines ?? []).map((l) => ({ diameterMm: l.diameterMm, weightKg: l.lineWeightKg }));
}
